import useAnalyticsPage from '../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../web-analytics/Page';
import React, { useState } from 'react';
import { useFetchedBillingInvoices } from '../hooks/useFetchedBillingInvoices';
import { useFetchedBillingCustomer } from '../hooks/useFetchedBillingCustomer';
import { useFetchedBillingPaymentMethods } from '../hooks/useFetchedBillingPaymentMethods';
import { BillingDetailsSection } from '../components/BillingDetailsSection';
import { InvoiceTable } from '../components/InvoiceTable';
import { PaymentMethodsTable } from '../components/PaymentMethodsTable';
import { PlanBenefits } from '../components/PlanBenefits';
import { BillingHeader } from '../components/BillingHeader';
import { v4 as uuidv4 } from 'uuid';
import { UpgradeToAnnualPlan } from '../components/UpgradeToAnnualPlan';
import { CancellationWizard } from '../components/cancellation/CancellationWizard';
import { useFinalizeInvoicesMutation } from '../hooks/useFinalizeInvoicesMutation';
import { FullScreenSpinner } from '../../commons/components/Spinner';
import { PersonalReferralLink } from '../components/RequestReferralLink';
import { billingPortalConfig } from '../../commons/config';
import { UnpauseSubscriptionBanner } from '../components/pause/UnpauseSubscriptionBanner';
import { UnpauseConfirmationModal } from '../components/pause/UnpauseConfirmationModal';
import { isSubscriptionPaused, isSubscriptionPauseScheduled } from '../utils';

export const BillingPortal = (props: { source: string }) => {
  const { source } = props;
  useAnalyticsPage(Page.BILLING, { source });

  const [cancellationWizardKey, setCancellationWizardKey] = useState<string | null>(null);
  const [unpauseModalKey, setUnpauseModalKey] = useState<number | null>(null);

  const {
    queryStatusComponent: customerStatusComponent,
    query: { data: customer, isLoading: isCustomerLoading, isError: isCustomerFetchError },
  } = useFetchedBillingCustomer({ withTracking: true, refetchOnWindowFocus: 'always' });

  const {
    queryStatusComponent: invoicesStatusComponent,
    query: { data: invoices, isLoading: isInvoicesLoading, isError: isInvoicesFetchError },
  } = useFetchedBillingInvoices({ withTracking: true, refetchOnWindowFocus: 'always' });

  const {
    queryStatusComponent: paymentMethodsStatusComponent,
    query: {
      data: paymentMethodsFetchData,
      isLoading: isPaymentMethodsLoading,
      isError: isPaymentMethodsFetchError,
    },
  } = useFetchedBillingPaymentMethods({ withTracking: true });

  const [expanded, setExpanded] = useState(false);

  const { mutate: finalizeInvoices, status: finalizeInvoicesStatus } =
    useFinalizeInvoicesMutation();

  if ((isCustomerLoading || isCustomerFetchError) && !customer)
    return <div className="billing-error">{customerStatusComponent}</div>;
  if ((isInvoicesLoading || isInvoicesFetchError) && !invoices)
    return <div className="billing-error">{invoicesStatusComponent}</div>;
  if ((isPaymentMethodsLoading || isPaymentMethodsFetchError) && !paymentMethodsFetchData)
    return <div className="billing-error">{paymentMethodsStatusComponent}</div>;

  const showEinBanner = customer?.taxId?.status === 'UNKNOWN';
  const showReferralLink = billingPortalConfig.isReferralLinkFeatureEnabled;
  const now = new Date();
  return (
    <div className="billing-container">
      <>
        <div>
          {customer &&
            customer.activeSubscription &&
            !customer.activeSubscription.cancelAt &&
            (isSubscriptionPaused(customer.activeSubscription, now) ||
              isSubscriptionPauseScheduled(customer.activeSubscription, now)) && (
              <UnpauseSubscriptionBanner activeSubscription={customer.activeSubscription} />
            )}
          {!!customer && !!invoices && (
            <div>
              <BillingHeader
                customer={customer}
                invoices={invoices}
                onCancelClick={() => {
                  finalizeInvoices();
                  setCancellationWizardKey(uuidv4());
                }}
                onUnpauseClick={() => setUnpauseModalKey(new Date().valueOf())}
                onSeeAnnualPlan={() => setExpanded(true)}
                showEinBanner={showEinBanner}
              />
            </div>
          )}

          {showReferralLink && customer && (
            <div style={{ marginTop: '24px' }} className="d-flex">
              <div className="ml-auto mr-auto">
                <PersonalReferralLink customer={customer} canHide={true} source="billing" />
              </div>
            </div>
          )}

          <PlanBenefits />

          {!!customer && (
            <div className="billing__upgrade-to-annual-wrapper">
              <UpgradeToAnnualPlan
                expanded={expanded}
                onExpandedChanged={setExpanded}
                hasPendingPlanChange={!!customer.pendingPlanChange}
                changePlanOptions={customer.changePlanOptions}
              />
            </div>
          )}

          {!!paymentMethodsFetchData && !!customer && (
            <div className="billing-section">
              <PaymentMethodsTable paymentMethods={paymentMethodsFetchData} customer={customer} />
            </div>
          )}

          {!!customer && (
            <div className="billing-section">
              <BillingDetailsSection customer={customer} />
            </div>
          )}

          {!!invoices && (
            <div className="billing-section">
              <InvoiceTable invoices={invoices} header="Invoices" context="all-invoices" />
            </div>
          )}
        </div>
        {cancellationWizardKey && finalizeInvoicesStatus === 'success' && (
          <CancellationWizard key={cancellationWizardKey} />
        )}
        {unpauseModalKey && <UnpauseConfirmationModal key={unpauseModalKey} />}
        {finalizeInvoicesStatus === 'loading' && <FullScreenSpinner />}
        {!!customer && (
          <div className="growegy-title10" style={{ maxWidth: '1200px' }}>
            Growegy is not a credit repair organization, financial advisor, financial planner,
            investment advisor, tax preparer, or acting as a fiduciary, as those or similar terms
            may be defined under federal or state law. Growegy makes recommendations you may find
            helpful. Growegy reports business tradelines to business credit bureaus. It is up to you
            to make the final decision about what is in your and your business's financial interest.
          </div>
        )}
      </>
    </div>
  );
};
